import { DeleteStudentDialog } from '@/components/students/DeleteStudentDialog';
import { StudentDialog } from '@/components/students/StudentDialog';
import Page, {
  PageHeader,
  PageHeaderActions,
  PageHeaderContent,
  PageHeaderDescription,
  PageHeaderTitle,
} from '@/components/Page';
import {
  useArchiveStudent,
  useDeleteStudent,
  useStudent,
  useUpdateStudent,
} from '@/hooks/useStudents';
import type { AppContext } from '@/main';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Badge } from '@ui/components/badge';
import { Button } from '@ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ui/components/card';
import {
  Archive,
  BookOpen,
  Calendar,
  Edit2,
  FileText,
  GraduationCap,
  Trash2,
  User,
} from 'lucide-react';
import { useState } from 'react';

export const Route = createFileRoute('/_dashboard/students/$studentId')<AppContext>({
  component: RouteComponent,
  loader: async ({ params }) => {
    return {
      crumb: `Student ${params.studentId} Details`,
    };
  },
});

function RouteComponent() {
  const { studentId } = Route.useParams();
  const { data: student, isLoading, error } = useStudent(studentId);
  const updateStudentMutation = useUpdateStudent();
  const deleteStudentMutation = useDeleteStudent();
  const archiveStudentMutation = useArchiveStudent();

  // Dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    studentId: null as string | null,
    studentName: '',
  });

  const handleUpdateStudent = async (data: { name: string }) => {
    await updateStudentMutation.mutateAsync({ studentId, data });
  };

  const handleDeleteStudent = async (studentId: string) => {
    await deleteStudentMutation.mutateAsync(studentId);
    // Navigate back to students list
    window.location.href = '/students';
  };

  const openDeleteDialog = () => {
    if (!student) return;
    setDeleteDialog({
      open: true,
      studentId: student.id,
      studentName: student.name,
    });
  };

  const handleArchive = async () => {
    if (confirm('Are you sure you want to archive this student?')) {
      try {
        await archiveStudentMutation.mutateAsync(studentId);
      } catch (error) {
        console.error('Failed to archive student:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading student details...</div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="text-destructive">Failed to load student details</div>
        <Button asChild variant="outline">
          <Link to="/students">Back to Students</Link>
        </Button>
      </div>
    );
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderContent>
          <div className="flex items-center gap-2">
            <PageHeaderTitle>{student.name}</PageHeaderTitle>
            {student.archived && (
              <Badge variant="secondary" className="gap-1">
                <Archive className="h-3 w-3" />
                Archived
              </Badge>
            )}
          </div>
          <PageHeaderDescription>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Created {new Date(student.createdAt).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Updated {new Date(student.updatedAt).toLocaleDateString()}
              </div>
            </div>
          </PageHeaderDescription>
        </PageHeaderContent>
        <PageHeaderActions>
          <Button
            variant="outline"
            disabled={student.archived}
            onClick={handleArchive}
            className="gap-2"
          >
            <Archive className="h-4 w-4" />
            {student.archived ? 'Archived' : 'Archive'}
          </Button>
          <Button variant="destructive" onClick={openDeleteDialog} className="gap-2">
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
          <Button onClick={() => setIsEditDialogOpen(true)} className="gap-2">
            <Edit2 className="h-4 w-4" />
            Edit
          </Button>
        </PageHeaderActions>
      </PageHeader>

      {/* Student Overview Cards */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Enrolled Courses</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{student.courses?.length || 0}</div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Assigned Exams</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{student.exams?.length || 0}</div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            <User className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {student.archived ? (
                <span className="text-orange-600">Archived</span>
              ) : (
                <span className="text-green-600">Active</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Courses and Exams Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Courses */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Enrolled Courses
            </CardTitle>
          </CardHeader>
          <CardContent>
            {student.courses && student.courses.length > 0 ? (
              <div className="space-y-2">
                {student.courses.map((courseId) => (
                  <div
                    key={courseId}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <GraduationCap className="h-4 w-4 text-muted-foreground" />
                      <Link
                        to="/courses/$courseId"
                        params={{ courseId }}
                        className="hover:underline"
                      >
                        Course {courseId.slice(0, 8)}...
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No courses enrolled</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Exams */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Assigned Exams
            </CardTitle>
          </CardHeader>
          <CardContent>
            {student.exams && student.exams.length > 0 ? (
              <div className="space-y-2">
                {student.exams.map((examId) => (
                  <div
                    key={examId}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <Link to="/exams/$examId" params={{ examId }} className="hover:underline">
                        Exam {examId.slice(0, 8)}...
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No exams assigned</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      <StudentDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        student={student}
        onSave={handleUpdateStudent}
      />

      <DeleteStudentDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((prev) => ({ ...prev, open }))}
        studentId={deleteDialog.studentId}
        studentName={deleteDialog.studentName}
        onDelete={handleDeleteStudent}
      />
    </Page>
  );
}
